import { useEffect, useState } from "react";
import { ChevronDown, X } from "lucide-react";
import { useI18n } from "../lib/i18n";

function Section({ title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="flex flex-col gap-3 border-b border-border pb-5">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center justify-between text-xs font-medium text-foreground"
        data-testid={`filter-section-${title.toLowerCase().replace(/\s+/g, "-")}`}
      >
        {title}
        <ChevronDown size={13} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="flex flex-col gap-2">{children}</div>}
    </div>
  );
}

function CheckList({ options, selected, onToggle, testidPrefix, emptyLabel }) {
  if (!options?.length) return <p className="text-xs text-muted-foreground">{emptyLabel}</p>;
  return (
    <div className="flex flex-col gap-1.5 max-h-52 overflow-y-auto no-scrollbar pr-1">
      {options.slice(0, 80).map((opt) => {
        const isOn = selected.includes(opt);
        return (
          <label
            key={opt}
            className={`flex items-center gap-2 text-sm cursor-pointer transition-colors ${isOn ? "text-[hsl(var(--accent))]" : "text-muted-foreground hover:text-foreground"}`}
          >
            <input
              type="checkbox"
              checked={isOn}
              onChange={() => onToggle(opt)}
              className="w-3.5 h-3.5 accent-[hsl(var(--accent))]"
              data-testid={`${testidPrefix}-${opt}`}
            />
            <span className="line-clamp-1">{opt}</span>
          </label>
        );
      })}
    </div>
  );
}

export default function Filters({ facets, params, setParams, onClose }) {
  const { t } = useI18n();

  const SORTS = [
    { value: "recent", label: t("sort.recent") },
    { value: "price_asc", label: t("sort.priceAsc") },
    { value: "price_desc", label: t("sort.priceDesc") },
    { value: "year_asc", label: t("sort.yearAsc") },
    { value: "year_desc", label: t("sort.yearDesc") },
    { value: "artist_asc", label: t("sort.artistAsc") },
  ];

  const toggleMulti = (key, value) => {
    const current = (params[key] || "").split(",").filter(Boolean);
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    setParams({ ...params, [key]: next.join(","), page: 1 });
  };

  const clearAll = () => setParams({ sort: params.sort || "recent", page: 1 });
  const get = (k) => (params[k] || "").split(",").filter(Boolean);
  const empty = t("filters.noOptions");

  return (
    <aside
      className="flex flex-col gap-6 w-full md:w-72 shrink-0 md:border-r md:border-border md:pr-8"
      data-testid="filters-panel"
    >
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium">{t("catalog.filters")}</h2>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={clearAll}
            className="text-xs text-muted-foreground hover:text-foreground"
            data-testid="filters-clear"
          >
            {t("catalog.clear")}
          </button>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="md:hidden p-1"
              aria-label="Close"
              data-testid="filters-close-mobile"
            >
              <X size={18} />
            </button>
          )}
        </div>
      </div>

      <input
        type="text"
        value={params.q || ""}
        onChange={(e) => setParams({ ...params, q: e.target.value, page: 1 })}
        placeholder={t("catalog.search")}
        className="w-full bg-transparent border border-border px-3 py-2.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:border-foreground"
        data-testid="filters-search"
      />

      <Section title={t("catalog.sort")}>
        <select
          value={params.sort || "recent"}
          onChange={(e) => setParams({ ...params, sort: e.target.value, page: 1 })}
          className="w-full bg-transparent border border-border px-3 py-2 text-sm focus:outline-none focus:border-foreground"
          data-testid="filters-sort"
        >
          {SORTS.map((s) => (<option key={s.value} value={s.value}>{s.label}</option>))}
        </select>
      </Section>

      <Section title={t("catalog.price")}>
        <div className="flex items-center gap-2">
          <input
            type="number" value={params.min_price || ""}
            onChange={(e) => setParams({ ...params, min_price: e.target.value, page: 1 })}
            placeholder={t("catalog.min")}
            className="w-full bg-transparent border border-border px-2 py-2 text-sm"
            data-testid="filters-min-price"
          />
          <span className="text-muted-foreground">—</span>
          <input
            type="number" value={params.max_price || ""}
            onChange={(e) => setParams({ ...params, max_price: e.target.value, page: 1 })}
            placeholder={t("catalog.max")}
            className="w-full bg-transparent border border-border px-2 py-2 text-sm"
            data-testid="filters-max-price"
          />
        </div>
      </Section>

      <Section title={t("catalog.genre")}>
        <CheckList options={facets?.genres || []} selected={get("genre")} onToggle={(v) => toggleMulti("genre", v)} testidPrefix="filter-genre" emptyLabel={empty} />
      </Section>
      <Section title={t("catalog.style")} defaultOpen={false}>
        <CheckList options={facets?.styles || []} selected={get("style")} onToggle={(v) => toggleMulti("style", v)} testidPrefix="filter-style" emptyLabel={empty} />
      </Section>
      <Section title={t("catalog.format")} defaultOpen={false}>
        <CheckList options={facets?.formats || []} selected={get("fmt")} onToggle={(v) => toggleMulti("fmt", v)} testidPrefix="filter-format" emptyLabel={empty} />
      </Section>
      <Section title={t("catalog.decade")} defaultOpen={false}>
        <CheckList options={(facets?.decades || []).map((d) => String(d))} selected={get("decade")} onToggle={(v) => toggleMulti("decade", v)} testidPrefix="filter-decade" emptyLabel={empty} />
      </Section>
      <Section title={t("catalog.mediaCond")} defaultOpen={false}>
        <CheckList options={facets?.media_conditions || []} selected={get("media_condition")} onToggle={(v) => toggleMulti("media_condition", v)} testidPrefix="filter-media" emptyLabel={empty} />
      </Section>
      <Section title={t("catalog.sleeveCond")} defaultOpen={false}>
        <CheckList options={facets?.sleeve_conditions || []} selected={get("sleeve_condition")} onToggle={(v) => toggleMulti("sleeve_condition", v)} testidPrefix="filter-sleeve" emptyLabel={empty} />
      </Section>
      <Section title={t("catalog.country")} defaultOpen={false}>
        <CheckList options={facets?.countries || []} selected={get("country")} onToggle={(v) => toggleMulti("country", v)} testidPrefix="filter-country" emptyLabel={empty} />
      </Section>
      <Section title={t("catalog.label")} defaultOpen={false}>
        <input
          type="text"
          value={params.label || ""}
          onChange={(e) => setParams({ ...params, label: e.target.value, page: 1 })}
          placeholder={t("catalog.labelSearch")}
          className="w-full bg-transparent border border-border px-3 py-2 text-sm"
          data-testid="filter-label-input"
        />
      </Section>
    </aside>
  );
}
